import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from "react";
import { Progress, message } from "antd";
import { Col, Row } from "antd";
import { DeleteModal } from "components/Dialog";
import styled from "styled-components";
import COLOR from "utils/constants/color";
import { getStorage, ref as fireRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import moment from 'moment';
import { v4 } from 'uuid';
import { FileUploader } from "react-drag-drop-files";
import { AddToPhotos, Close, Delete, PictureAsPdf, Sync } from '@mui/icons-material';
import { Button, IconButton, Modal } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import BadgeButton from '../BadgeButton';

const ModalContent = styled.div`
  left: 50%;
  width: 80vw;
  max-width: 70%;
  padding: 0px 24px;
  position: absolute;
  min-height: 350px;
  max-height: 95vh;
  overflow-y: auto;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: #fff;
  padding: 20px; 
    @media only screen and (max-width: 600px) {
        width: 95vw !important;
    }
`;

const ModalConfigArea = styled.div`
  max-height: 75vh;
  overflow: auto;
//   margin-top: 10px;
`;

const TitleText = styled.span`
    font-size: 18px;
    color: black;
    font-weight:600;
`;

const FileUploadArea = styled.div`
    border: 2px dashed ${COLOR.grey100};
    border-radius: 8px;
    padding: 40px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s;
    &:hover {
        border-color: ${COLOR.blue100};
        background-color: ${COLOR.grey50};
    }
`;

const PDFPreviewArea = styled.div`
    border: 1px solid ${COLOR.grey100};
    border-radius: 8px;
    padding-left: 10px;
    padding-right: 10px;
    padding-top: 5px;
    padding-bottom: 5px;
    margin-top: 5px;
    background-color: ${COLOR.grey50};
    position: relative;
`;

const PDFViewer = styled.iframe`
    width: 100%;
    height: 70vh;
    border: none;
    border-radius: 4px;
    background-color: white;
`;

const DeleteButtonContainer = styled.div`
    position: absolute;
    top: 15px;
    right: 15px;
    background-color: white;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 10;
`;

const Text = styled.span`
    color: ${COLOR.grey300};
    font-size: 14px;
`;

const LoadingOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.95);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 100;
    border-radius: 8px;
`;

const useStyles = makeStyles((theme) => ({
    formControl: {
        minWidth: 120,
    },
}));

const PDF_FILE_TYPES = ["PDF"];

const PDFUploader = ({ bookId, pageId, pdfData, onChangePDF, onRemovePDF, hasPermission = true }) => {
    const showAlert = useAlert();
    const classes = useStyles();
    const [openModal, setOpenModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentPDF, setCurrentPDF] = useState(null);
    const fileInputRef = React.useRef(null);

    const hasPDF = pdfData && pdfData.length > 0 && pdfData.find(p => p.pageId === pageId);

    useEffect(() => {
        if (hasPDF) {
            setCurrentPDF(hasPDF);
        } else {
            setCurrentPDF(null);
        }
    }, [pdfData, pageId]);

    const handlePDFChange = (file) => {
        uploadPDFToStorage(file);
    };

    const handleReuploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const uploadPDFToStorage = async (file) => {
        if (!file) {
            showAlert("No file selected", "error");
            return;
        }

        try {
            setLoading(true);
            const storage = getStorage();
            const location = '/books/pdfs';
            const id = `${moment().format('YYYY-MM-DD')}-${v4()}`;
            const storageRef = fireRef(storage, `${location}/${id}-${file.name}`);
            
            const uploadTask = uploadBytesResumable(storageRef, file);
            
            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                    setUploadProgress(progress);
                },
                (error) => {
                    Sentry.captureException(`Error uploading PDF: ${error}`);
                    console.error("Upload error:", error);
                    showAlert("Failed to upload PDF", "error");
                    setLoading(false);
                    setUploadProgress(0);
                },
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        setLoading(false);
                        setUploadProgress(0);
                        onChangePDF({ 
                            url: downloadURL, 
                            pageId: pageId,
                            fileName: file.name 
                        });
                        // showAlert("PDF uploaded successfully!", "success");
                    } catch (error) {
                        Sentry.captureException(`Error getting PDF download URL: ${error}`);
                        console.error("Error getting download URL:", error);
                        showAlert("Failed to get PDF URL", "error");
                        setLoading(false);
                        setUploadProgress(0);
                    }
                }
            );
        } catch (error) {
            Sentry.captureException(`Error in uploadPDFToStorage: ${error}`);
            console.error("Upload error:", error);
            showAlert("Something went wrong!", "error");
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const onDelete = async () => {
        setDeleteLoader(true);
        try {
            await onRemovePDF(currentPDF);
            // showAlert("PDF removed successfully", "success");
            setCurrentPDF(null);
        } catch (error) {
            Sentry.captureException(`Error deleting PDF: ${error}`);	
            console.error("Error removing PDF:", error);
            // showAlert("Failed to remove PDF", "error");
        } finally {
            setDeleteLoader(false);
            setDeleteModal(false);
        }
    };

    const onClose = () => {
        setOpenModal(false);
    };

    return (
        <>
            <BadgeButton
                onClick={() => {
                    if (!hasPermission) {
                        message.warning('You do not have permission to access this feature.');
                        return;
                    }
                    setOpenModal(true);
                }}
                startIcon={<PictureAsPdf />}
                showBadge={hasPDF}
                backgroundColor="#e74c3c"
            >
                PDF Uploader
            </BadgeButton>
            
            <DeleteModal
                deleteToggle={deleteModal}
                loading={deleteLoader}
                onDelete={onDelete}
                setDeleteToggle={setDeleteModal}
                message={`Are you sure you want to remove the PDF?`}
            />

            <Modal
                open={openModal}
                onClose={onClose}
                style={{ height: "100%", width: "100%" }}
            >
                <ModalContent>
                    <Row style={{ justifyContent: "space-between", alignItems: "center" }}>
                        <TitleText>PDF Uploader</TitleText>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {currentPDF && currentPDF.resource ? (
                            <>
                                <Button
                                    onClick={handleReuploadClick}
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    startIcon={<Sync />}
                                    disabled={loading}
                                >
                                    Replace
                                </Button>
                                <Button
                                    onClick={() => setDeleteModal(true)}
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    startIcon={<Delete />}
                                    disabled={loading}
                                >
                                    Delete
                                </Button>
                            </>
                        ) : null}
                        <IconButton
                            onClick={onClose}
                            style={{ padding: 0 }}
                        >
                            <Close color="secondary" />
                        </IconButton>
                        </div>
                    </Row>

                    <ModalConfigArea>
                        {currentPDF && currentPDF.resource ? (
                            <PDFPreviewArea>
                                {loading && (
                                    <LoadingOverlay>
                                        <Sync color="primary" style={{ fontSize: 48, marginBottom: 20 }} className="rotating-icon" />
                                        <Progress 
                                            type="line" 
                                            percent={uploadProgress} 
                                            size="default"
                                            style={{ width: '60%', maxWidth: '400px' }}
                                        />
                                        <Text style={{ marginTop: 15, fontSize: 16, color: COLOR.grey400 }}>
                                            Replacing PDF... {uploadProgress}%
                                        </Text>
                                    </LoadingOverlay>
                                )}
                                <PDFViewer
                                    src={`${currentPDF.resource}#toolbar=0&navpanes=0&scrollbar=1`}
                                    title="PDF Preview"
                                />
                            </PDFPreviewArea>
                        ) : (
                            <FileUploader
                                handleChange={handlePDFChange}
                                name="pdf_file"
                                types={PDF_FILE_TYPES}
                                disabled={loading}
                                onTypeError={() => showAlert("Only PDF files are allowed. Please select a valid PDF file.", "error")}
                            >
                                <FileUploadArea>
                                    {loading && (
                                        <Progress 
                                            type="line" 
                                            percent={uploadProgress} 
                                            size="small" 
                                            style={{ marginBottom: 20 }} 
                                        />
                                    )}
                                    <Row style={{ justifyContent: "center", alignItems: "center", gap: "10px" }}>
                                        <Col>
                                            <AddToPhotos color="primary" style={{ fontSize: 32 }} />
                                        </Col>
                                    </Row>
                                    <Row style={{ marginTop: 10 }}>
                                        <Col span={24}>
                                            <Text>
                                                {loading ? "Uploading PDF..." : "Upload or drop a PDF file here"}
                                            </Text>
                                        </Col>
                                    </Row>
                                    <Row style={{ marginTop: 10 }}>
                                        <Col span={24}>
                                            <Text style={{ fontSize: 12 }}>
                                                Only PDF files are allowed (1 per page)
                                            </Text>
                                        </Col>
                                    </Row>
                                </FileUploadArea>
                            </FileUploader>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    if (file.type === 'application/pdf') {
                                        handlePDFChange(file);
                                    } else {
                                        showAlert("Only PDF files are allowed. Please select a valid PDF file.", "error");
                                    }
                                }
                                e.target.value = '';
                            }}
                        />
                    </ModalConfigArea>
                </ModalContent>
            </Modal>
        </>
    );
};

export default PDFUploader;

